import type { ComponentType } from 'react';
import {
  AppstoreOutlined,
  BookOutlined,
  BuildOutlined,
  CarOutlined,
  DesktopOutlined,
  FileTextOutlined,
  FireOutlined,
  FolderOutlined,
  OrderedListOutlined,
  PhoneOutlined,
  PictureOutlined,
  QuestionCircleOutlined,
  RiseOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  ShopOutlined,
  SoundOutlined,
  TeamOutlined,
  ToolOutlined,
  WalletOutlined,
} from '@ant-design/icons';

/**
 * Иконки разделов БЗ. КЛЮЧ (хранится в WikiBlock.icon после миграции Teamly) →
 * AntD-иконка. Так разделы получают «китовые» line-иконки, а не сырой текст ключа.
 *
 * ВАЖНО: общий источник правды для всех экранов, где рисуется иконка раздела
 * (лендинг БЗ, герой страницы раздела, селектор блоков в редакторе и т.д.).
 * Раньше каждый экран либо имел свою копию маппинга, либо выводил block.icon
 * сырым текстом → на странице раздела торчало литеральное «build» вместо иконки.
 */
export const SECTION_ICONS: Record<string, ComponentType> = {
  build: BuildOutlined,             // Открытие точки
  shop: ShopOutlined,               // Работа действующей точки
  rise: RiseOutlined,               // Управление и развитие
  sound: SoundOutlined,             // Маркетинг
  fire: FireOutlined,               // Кухня
  phone: PhoneOutlined,             // Колл-центр
  wallet: WalletOutlined,           // Финансы
  desktop: DesktopOutlined,         // CRM / IT
  question: QuestionCircleOutlined, // FAQ
  rocket: RocketOutlined,
  book: BookOutlined,
  team: TeamOutlined,
  safety: SafetyCertificateOutlined,
  car: CarOutlined,
  picture: PictureOutlined,
  tool: ToolOutlined,
  folder: FolderOutlined,
  appstore: AppstoreOutlined,
};

/** Иконки, доступные в пикере при создании/редактировании раздела. */
export const ICON_CHOICES: string[] = [
  'build', 'shop', 'rise', 'sound', 'fire', 'phone', 'wallet', 'desktop',
  'question', 'rocket', 'team', 'safety', 'car', 'picture', 'tool', 'book',
];

/**
 * Иконка раздела: известный ключ → AntD-иконка; эмодзи/произвольный текст →
 * как есть (фолбэк для старых блоков); пусто/неизвестно → книга.
 */
export function renderSectionIcon(icon: string | null | undefined): React.ReactNode {
  if (icon && SECTION_ICONS[icon]) {
    const Ic = SECTION_ICONS[icon];
    return <Ic />;
  }
  if (icon && icon.trim()) return icon;
  return <BookOutlined />;
}

// ── HUB 2.0 (волна W2): общие для экранов базы знаний ────────────────

/** hex цвета раздела → мягкая подложка плитки (rgba .12); не-hex → оранжевая. */
export function sectionSoft(hex: string | null | undefined): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return 'var(--fk-orange-soft)';
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, 0.12)`;
}

/** Цвет иконки раздела: валидный hex раздела, иначе оранжевый токен. */
export function sectionAccent(hex: string | null | undefined): string {
  return hex && /^#?[0-9a-f]{6}$/i.test(hex) ? (hex.startsWith('#') ? hex : `#${hex}`) : 'var(--fk-orange)';
}

/** Подписи типов материала (article.type). */
export const MATERIAL_LABELS: Record<string, string> = {
  article: 'Статья',
  instruction: 'Инструкция',
  checklist: 'Чек-лист',
  regulation: 'Регламент',
  faq: 'FAQ',
  standard: 'Стандарт',
  publication: 'Публикация',
  wizard_step: 'Шаг открытия',
  decision_tree: 'Дерево решений',
  document_template: 'Шаблон документа',
};

/** Чипы типов на главной БЗ, в разделе и поиске (null — все типы). */
export const TYPE_CHIPS: Array<{ label: string; type: string | null }> = [
  { label: 'Все', type: null },
  { label: 'Инструкции', type: 'instruction' },
  { label: 'Чек-листы', type: 'checklist' },
  { label: 'Регламенты', type: 'regulation' },
  { label: 'Статьи', type: 'article' },
  { label: 'FAQ', type: 'faq' },
];

/** Иконка по типу материала (только @ant-design/icons). */
export function materialIcon(type: string | null | undefined): React.ReactNode {
  switch (type) {
    case 'instruction':
      return <BookOutlined />;
    case 'checklist':
      return <OrderedListOutlined />;
    case 'regulation':
    case 'standard':
      return <SafetyCertificateOutlined />;
    case 'faq':
      return <QuestionCircleOutlined />;
    default:
      return <FileTextOutlined />;
  }
}
